import React from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { spacing } from "../../styles/theme";
import {
  SidebarItem,
  SpecsInputField,
  SpecsMultiAutocompleteField,
} from "./SpecsEditorFields/index";
import { useSpecsEditorContextValue } from "./useSpecsEditor";
import { useSpecsModelSelector } from "./SpecsModelContext";

export default function SpecsPositionGroupEditor() {
  const {
    editor: {
      identity: { productVersion },
    },
  } = useSpecsEditorContextValue();
  const currentVersion = useSpecsModelSelector(
    (model) => model.getVersion(productVersion) ?? null
  );
  const rawPositionGroups = useSpecsModelSelector((model) =>
    structuredClone(model.getVersion(productVersion)?.getPositionGroups() ?? [])
  );
  const [selectedGroupName, setSelectedGroupName] = React.useState<
    string | null
  >(null);
  const [groupNameDraft, setGroupNameDraft] = React.useState("");
  const selectedGroupPath = useSpecsModelSelector((model) => {
    const versionIndex = model.product.versions.findIndex(
      (version) => version.name === productVersion
    );
    const version = model.product.versions[versionIndex];

    if (!version || !selectedGroupName) {
      return null;
    }

    const groupIndex = version.positionGroups.findIndex(
      (group: any) => group?.name === selectedGroupName
    );

    if (groupIndex === -1) {
      return null;
    }

    return `window.specs.model.product.versions[${versionIndex}].positionGroups[${groupIndex}]`;
  });
  const selectedGroup =
    rawPositionGroups.find((group: any) => group.name === selectedGroupName) ??
    null;
  const groupUnavailableWith = React.useMemo(
    () => selectedGroup?.unavailableWith ?? [],
    [selectedGroup]
  );
  const groupNameOptions = React.useMemo(
    () =>
      Array.from(
        new Set(
          rawPositionGroups.map((group: any) => group.name).filter(Boolean)
        )
      )
        .filter(
          (groupName): groupName is string => typeof groupName === "string"
        )
        .sort(),
    [rawPositionGroups]
  );

  React.useEffect(() => {
    if (!rawPositionGroups.length) {
      if (selectedGroupName !== null) {
        setSelectedGroupName(null);
      }
      return;
    }

    if (
      !selectedGroupName ||
      !rawPositionGroups.some((group: any) => group.name === selectedGroupName)
    ) {
      setSelectedGroupName(rawPositionGroups[0].name);
    }
  }, [rawPositionGroups, selectedGroupName]);

  React.useEffect(() => {
    setGroupNameDraft(selectedGroup?.name ?? "");
  }, [selectedGroup?.name]);

  const groupNameDraftTrimmed = groupNameDraft.trim();
  const isGroupNameChanged = groupNameDraftTrimmed !== selectedGroup?.name;
  const isGroupNameDuplicate =
    isGroupNameChanged && groupNameOptions.includes(groupNameDraftTrimmed);
  let groupNameError = "";

  if (!groupNameDraftTrimmed) {
    groupNameError = "Le nom du groupe est requis";
  } else if (isGroupNameDuplicate) {
    groupNameError = "Ce nom de groupe existe déjà";
  }

  const commitGroup = React.useCallback(
    (nextGroup: any) => {
      if (!currentVersion || !selectedGroup) return;

      currentVersion.updatePositionGroup(selectedGroup.name, nextGroup);
      if (selectedGroup.name !== nextGroup.name) {
        setSelectedGroupName(nextGroup.name);
      }
    },
    [currentVersion, selectedGroup]
  );

  const commitGroupName = React.useCallback(() => {
    if (!selectedGroup || groupNameError) {
      return;
    }

    const nextGroupName = groupNameDraft.trim();
    if (nextGroupName === selectedGroup.name) {
      return;
    }

    commitGroup({
      ...selectedGroup,
      name: nextGroupName,
    });
  }, [commitGroup, groupNameDraft, groupNameError, selectedGroup]);

  const addGroup = React.useCallback(() => {
    if (!currentVersion) {
      return;
    }

    const stamp = Date.now();
    const nextGroup = {
      name: `group-${stamp}`,
      mutuallyExclusiveGroupNames: [],
      unavailableWith: [],
    };
    currentVersion.addPositionGroup(nextGroup);
    setSelectedGroupName(nextGroup.name);
  }, [currentVersion]);

  const deleteGroup = React.useCallback(
    (groupName: string) => {
      currentVersion?.removePositionGroup(groupName);
    },
    [currentVersion]
  );

  const addUnavailableRule = React.useCallback(() => {
    if (!selectedGroup) return;
    commitGroup({
      ...selectedGroup,
      unavailableWith: [
        ...groupUnavailableWith,
        { partName: "", elementName: "", styleName: "" },
      ],
    });
  }, [commitGroup, groupUnavailableWith, selectedGroup]);

  const deleteUnavailableRule = React.useCallback(
    (ruleIndex: number) => {
      if (!selectedGroup) return;
      commitGroup({
        ...selectedGroup,
        unavailableWith: groupUnavailableWith.filter(
          (_rule: any, index: number) => index !== ruleIndex
        ),
      });
    },
    [commitGroup, groupUnavailableWith, selectedGroup]
  );

  return (
    <Paper
      variant="outlined"
      sx={{ p: spacing.groupingDesktop, mb: spacing.groupingDesktop }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1.5 }}
      >
        <Typography variant="h2">Groupes de positions</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={addGroup}>
          Ajouter
        </Button>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "280px 1fr" },
          gap: 2,
        }}
      >
        <Stack spacing={0.5}>
          {rawPositionGroups.map((group: any) => (
            <SidebarItem
              key={group.name}
              label={group.name}
              active={selectedGroupName === group.name}
              onClick={() => setSelectedGroupName(group.name)}
              onDelete={() => deleteGroup(group.name)}
            />
          ))}
          {rawPositionGroups.length === 0 && (
            <Alert severity="info">Aucun groupe de positions.</Alert>
          )}
        </Stack>

        {selectedGroup ? (
          <Stack spacing={2}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 1.5,
              }}
            >
              <SpecsInputField
                label="Nom du groupe"
                debugPath={
                  selectedGroupPath ? `${selectedGroupPath}.name` : undefined
                }
                size="small"
                value={groupNameDraft}
                error={!!groupNameError}
                helperText={groupNameError || " "}
                onChange={(e) => setGroupNameDraft(e.target.value)}
                onBlur={commitGroupName}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    commitGroupName();
                  }
                }}
              />
              <SpecsMultiAutocompleteField
                label="Groupes mutuellement exclusifs"
                debugPath={
                  selectedGroupPath
                    ? `${selectedGroupPath}.mutuallyExclusiveGroupNames`
                    : undefined
                }
                value={selectedGroup.mutuallyExclusiveGroupNames ?? []}
                options={groupNameOptions.filter(
                  (groupName) => groupName !== selectedGroup.name
                )}
                placeholder="Sélectionner des groupes"
                onChange={(nextValues) =>
                  commitGroup({
                    ...selectedGroup,
                    mutuallyExclusiveGroupNames: nextValues,
                  })
                }
              />
            </Box>

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="subtitle2">Indisponible avec</Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={addUnavailableRule}
              >
                Ajouter une règle
              </Button>
            </Stack>
            {groupUnavailableWith.map((rule: any, ruleIndex: number) => (
              <Box
                key={`rule-${ruleIndex}`}
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "1fr 1fr 1fr auto",
                  },
                  gap: 1.5,
                  alignItems: "center",
                }}
              >
                <SpecsInputField
                  label="Part Name"
                  debugPath={
                    selectedGroupPath
                      ? `${selectedGroupPath}.unavailableWith[${ruleIndex}].partName`
                      : undefined
                  }
                  size="small"
                  value={rule.partName || ""}
                  onChange={(e) =>
                    commitGroup({
                      ...selectedGroup,
                      unavailableWith: groupUnavailableWith.map(
                        (currentRule: any, index: number) =>
                          index === ruleIndex
                            ? {
                                ...currentRule,
                                partName: e.target.value,
                              }
                            : currentRule
                      ),
                    })
                  }
                />
                <SpecsInputField
                  label="Element Name"
                  debugPath={
                    selectedGroupPath
                      ? `${selectedGroupPath}.unavailableWith[${ruleIndex}].elementName`
                      : undefined
                  }
                  size="small"
                  value={rule.elementName || ""}
                  onChange={(e) =>
                    commitGroup({
                      ...selectedGroup,
                      unavailableWith: groupUnavailableWith.map(
                        (currentRule: any, index: number) =>
                          index === ruleIndex
                            ? {
                                ...currentRule,
                                elementName: e.target.value,
                              }
                            : currentRule
                      ),
                    })
                  }
                />
                <SpecsInputField
                  label="Style Name"
                  debugPath={
                    selectedGroupPath
                      ? `${selectedGroupPath}.unavailableWith[${ruleIndex}].styleName`
                      : undefined
                  }
                  size="small"
                  value={rule.styleName || ""}
                  onChange={(e) =>
                    commitGroup({
                      ...selectedGroup,
                      unavailableWith: groupUnavailableWith.map(
                        (currentRule: any, index: number) =>
                          index === ruleIndex
                            ? {
                                ...currentRule,
                                styleName: e.target.value,
                              }
                            : currentRule
                      ),
                    })
                  }
                />
                <IconButton onClick={() => deleteUnavailableRule(ruleIndex)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
          </Stack>
        ) : (
          <Alert severity="info">
            Ajouter un groupe de positions ou en sélectionner un pour commencer.
          </Alert>
        )}
      </Box>
    </Paper>
  );
}
